import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Table } from 'antd';
import RoomConfigData, { ServiceDto, TranslationData } from '../../../../../../models/MappingData';
import { DeleteOutlined, EditOutlined, ImportOutlined } from '@ant-design/icons';
import { Row, Col, Button, Drawer } from 'antd';
import { deleteRoomConfigMapping, fetchRoomConfigMapping, getAllServices } from '../../../../../../api/repositoryEP';
import RoomForm from '../Forms/RoomForm/RoomForm';
import Search from 'antd/es/input/Search';
import { PageSize } from '../../../../../../utils/constants';
import { CustomPagination } from '../../../../..';
import { GenericModal } from '../../../../../common/Modal/GenericModal';
import LoadingDataComponent from '../../../../../common/LoadingData/LoadingDataComponent';


interface RoomConfigProps {
    language: string;
}

const RoomConfig = (props: RoomConfigProps): JSX.Element => {

    const { t, i18n } = useTranslation();
    const [data, setData] = useState<RoomConfigData[]>([]);
    const drawerWidth = window.innerWidth <= 768 ? '100%' : '40%';
    const [newRoomDrawer, setNewRoomDrawer] = useState<boolean>(false);
    const [editRoomDrawer, setEditRoomDrawer] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [roomData, setRoomData] = useState<RoomConfigData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);  // Estado para el indicador de carga

    const [actualPage, setActualPage] = useState<number>(1);
    const [actualPageSize, setActualPageSize] = useState<number>(PageSize);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [filter, setFilter] = useState<string>('');



    const onChangePagination = (newPage: number, newPageSize?: number | undefined) => {
        setActualPage(newPage);
        setActualPageSize(newPageSize ?? PageSize);
    }


    useEffect(() => {
        if (!newRoomDrawer && !editRoomDrawer && !deleteModalVisible) {
            setLoading(true);
            fetchRoomConfigMapping(filter, props.language, actualPage, actualPageSize).then((response) => {
                if (response?.type === 1) {
                    setData(response.data.elements);
                    setTotalRecords(response?.data.totalRecords as number);
                }
                setLoading(false);
            });
        }
    }, [newRoomDrawer, editRoomDrawer, deleteModalVisible, props.language, actualPage, actualPageSize, filter]);


    const columns = [
        {
            title: t("components.tab-mapping.room-config.table.c1"),
            dataIndex: 'name',
            key: 'name',
            render: (names: TranslationData[]) => {
                const normalizedLanguage = i18n.language.slice(0, 2).toLowerCase();

                const nameInLanguage = names.find((name: TranslationData) => name.language.slice(0, 2).toLowerCase() === normalizedLanguage);

                return nameInLanguage?.value || names[0]?.value || 'No name available';
            }
        },
        {
            title: t("components.tab-mapping.room-config.table.c2"),
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: t("components.tab-mapping.room-config.table.c3"),
            dataIndex: 'integrationCode',
            key: 'integrationCode',
        },
        {
            title: t("components.tab-mapping.room-config.table.c4"),
            dataIndex: 'internNote',
            key: 'internNote',
        },
        {
            title: t("components.tab-mapping.room-config.table.c4"),
            key: 'action',
            render: (obj: RoomConfigData) => (
                <div className='app-icon-table-container'>
                    <EditOutlined className='app-icon-table' onClick={() => editRoom(obj)} />
                    <DeleteOutlined className='app-icon-table' onClick={() => openDeleteModal(obj)} />
                </div>
            ),
        }
    ];


    const editRoom = (room: RoomConfigData) => {
        setRoomData(room);
        setEditRoomDrawer(true)
    };





    const onSearch = (value: string) => {
        setFilter(value);
        setActualPage(1); // 👈 Siempre empezamos en la primera página al buscar
    };


    const handleAddButton = () => {
        setRoomData(null);
        setNewRoomDrawer(true);

    }
    const [deletingRoom, setDeletingRoom] = useState<boolean>(false);

    const openDeleteModal = (room: RoomConfigData) => {
        setRoomData(room);
        setDeleteModalVisible(true);
    }

    const deleteRoom = async () => {
        if (roomData?.id) {
            setDeletingRoom(true);
            deleteRoomConfigMapping(roomData.id).then((r) => {
                if (r?.type === 1) {
                    setDeletingRoom(false);
                    setDeleteModalVisible(false);
                    setRoomData(null);
                } else {
                    setDeletingRoom(false);

                }
            });
        }
    };
    const cancelDeleteRoom = () => {
        setDeleteModalVisible(false);
        setRoomData(null);
    }

    const [servicesAvailable, setServicesAvailable] = useState<ServiceDto[]>([]);
    const [loadingServices, setLoadingServices] = useState<boolean>(false);

    useEffect(() => {
        if (newRoomDrawer || editRoomDrawer) {
            setLoadingServices(true);
            getAllServices(i18n.language)
                .then((response) => {
                    if (response?.type === 1) {
                        setServicesAvailable(response.data as ServiceDto[]);
                    }
                })
                .finally(() => {
                    setLoadingServices(false);
                });
        }

    }, [newRoomDrawer, editRoomDrawer]);







    return (
        <div className="room-config-component">
            {deleteModalVisible && roomData?.id && (
                <GenericModal
                    isLoading={deletingRoom}
                    title={t("components.tab-mapping.room-config.modal-title")}
                    closable
                    content={<div>{t("components.tab-mapping.room-config.modal-msg")}</div>}
                    visible={deleteModalVisible}
                    onOk={deleteRoom}
                    onCancel={cancelDeleteRoom}
                />
            )}


            <Drawer
                open={editRoomDrawer}
                title={t("components.tab-mapping.room-config.drawer.title-edit")}
                onClose={() => {
                    setRoomData(null)
                    setEditRoomDrawer(false)
                }}
                width={drawerWidth}
                closable>
                {loadingServices ? (
                    <LoadingDataComponent /> // o un spinner, skeleton, etc
                ) : (
                    <RoomForm
                        setDrawerVisible={setEditRoomDrawer}
                        drawerVisible={editRoomDrawer}
                        roomData={roomData}
                        setRoomData={setRoomData}
                        language={props.language}
                        servicesAvailable={servicesAvailable}

                    />
                )}

            </Drawer>

            <Drawer
                open={newRoomDrawer}
                title={t("components.tab-mapping.room-config.drawer.title-new")}
                onClose={() => {
                    setNewRoomDrawer(false);
                    setRoomData(null)
                }}
                width={drawerWidth}
                closable
            >
                {loadingServices ? (
                    <LoadingDataComponent /> // o un spinner, skeleton, etc
                ) : (
                    <RoomForm
                        setDrawerVisible={setNewRoomDrawer}
                        drawerVisible={newRoomDrawer}
                        roomData={null}
                        setRoomData={setRoomData}
                        language={props.language}
                        servicesAvailable={servicesAvailable}
                    />
                )}
            </Drawer>


            <Row gutter={[10, 10]} style={{ flexDirection: 'column' }}>
                <Col xs={24} md={24}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={24} style={{ fontFamily: 'Montserrat' }}>
                            {t("components.tab-mapping.room-config.msg-info")}
                        </Col>
                        <Col xs={24} md={24} lg={16}>
                            <Search
                                placeholder={t("components.tab-mapping.room-config.search") || ''}
                                allowClear
                                onSearch={onSearch}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={4}>
                            <Button
                                className='app-button'
                                onClick={() => handleAddButton()}>
                                {t("components.tab-mapping.room-config.button-add")}
                            </Button>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={4}>
                            <Button disabled className='app-button'><ImportOutlined /> {t("components.tab-mapping.room-config.button-import")}</Button>
                        </Col>
                    </Row>
                </Col>

                <Col xs={24} md={24}>
                    <Table
                        className='table'
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                    />
                </Col>
            </Row>
            {(
                totalRecords > 0 && (
                    <div style={{ marginTop: '15px' }}>
                        <CustomPagination
                            actualPage={actualPage}
                            actualPageSize={actualPageSize}
                            totalElements={totalRecords}
                            onChange={onChangePagination}
                        />
                    </div>
                )
            )}

        </div>
    );
};



export default RoomConfig;